import React, { useRef, useState } from 'react';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import Button from '../Button/Button';
import IconSuccess from '../IconSuccess/IconSuccess';

import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';

import css from './StripeConnectAccountStatusBox.module.css';
import { SWDialog } from '../mui/Dialog';
import { DialogContent, DialogTitle, IconButton, useMediaQuery } from '@mui/material';
import { useTheme } from '@emotion/react';

const STATUS_VERIFICATION_NEEDED = 'verificationNeeded';
const STATUS_VERIFICATION_SUCCESS = 'verificationSuccess';
const STATUS_VERIFICATION_ERROR = 'verificationError';

const StripeConnectAccountStatusBox = props => {
  const [isDialogOpen, setDialogOpen] = useState(false);
  const theme = useTheme();

  const [scroll, setScroll] = useState('paper');

  const descriptionElementRef = useRef(null);
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const { type, onGetStripeConnectAccountLink, inProgress, disabled } = props;

  const handleMoreInfoClick = () => {
    setDialogOpen(true);
  };
  const handleClose = () => {
    setDialogOpen(false);
  };

  const handleContactSupportClick = event => {
    event.preventDefault();
    // eslint-disable-next-line no-undef
    Tawk_API.toggle();
  };

  if (type === STATUS_VERIFICATION_NEEDED) {
    const translationValues = {
      b: msgFragment => <strong>{msgFragment}</strong>,
    };
    const contactSupportLink = (
      <a href="#" onClick={handleContactSupportClick} className={css.supportLink}>
        <FormattedMessage id="StripeConnectAccountStatusModal.link" />
      </a>
    );

    return (
      <div>
        <h3 className={css.verificationBoxHeadline}>
          <FormattedMessage id="StripeConnectAccountStatusBox.verificationNeededHeadline" />
          <a className={css.infoIon} onClick={handleMoreInfoClick}>
            <HelpOutlineIcon fontSize="small" />
          </a>
        </h3>

        <div className={classNames(css.verificiationBox, css.verficiationNeededBox)}>
          <div className={css.verificationTopRow}>
            <div className={css.verificiationBoxTextWrapper}>
              <div className={css.verificationBoxTitle}>
                <FormattedMessage id="StripeConnectAccountStatusBox.verificationNeededTitle" />
              </div>
              <div className={css.verificationBoxText}>
                <p>
                  <FormattedMessage id="StripeConnectAccountStatusBox.verificationNeededText" />
                </p>
                <ol className={css.verificationBoxList}>
                  <li>
                    <span>1.</span>
                    <FormattedMessage id="StripeConnectAccountStatusBox.verificationNeeded1" />
                  </li>
                  <li>
                    <span>2.</span>
                    <FormattedMessage id="StripeConnectAccountStatusBox.verificationNeeded2" />
                  </li>
                </ol>
              </div>
            </div>

            <Button
              rootClassName={css.getVerifiedButton}
              spinnerClassName={css.spinner}
              type="button"
              inProgress={inProgress}
              disabled={disabled}
              onClick={onGetStripeConnectAccountLink}
            >
              <FormattedMessage id="StripeConnectAccountStatusBox.getVerifiedButton" />
            </Button>
          </div>
          <div className={css.verificationFooter}>
            <p className={css.verificationFooterLine}>
              <FormattedMessage id="StripeConnectAccountStatusBox.verificationNeededFooter" />
            </p>
            <p className={css.verificationFooterLine}>
              <FormattedMessage
                id="StripeConnectAccountForm.stripeToSText"
                values={{
                  stripeConnectedAccountTermsLink: (
                    <a
                      href="https://stripe.com/connect-account/legal"
                      target="_blank"
                      rel="noopener noreferrer"
                      className={css.supportLink}
                    >
                      <FormattedMessage id="StripeConnectAccountForm.stripeConnectedAccountTermsLink" />
                    </a>
                  ),
                }}
              />
            </p>
          </div>
        </div>
        <SWDialog
          open={isDialogOpen}
          onClose={handleClose}
          fullScreen={fullScreen}
          PaperProps={{
            sx: { minWidth: { md: 630 }, maxHeight: '100vh', maxWidth: '800px' },
          }}
          scroll="paper"
          aria-labelledby="scroll-dialog-title"
          aria-describedby="scroll-dialog-description"
        >
          <DialogTitle id="scroll-dialog-title">
            <FormattedMessage id="StripeConnectAccountStatusModal.header" />
          </DialogTitle>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={theme => ({
              position: 'absolute',
              right: 8,
              top: 8,
              color: theme.palette.grey[500],
            })}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent dividers={scroll === 'paper'} ref={descriptionElementRef} tabIndex={-1}>
            <div className={css.dialogContent}>
              <p>
                <FormattedMessage id="StripeConnectAccountStatusModal.title" />
              </p>
              <ul>
                <li>
                  <FormattedMessage
                    id="StripeConnectAccountStatusModal.text1"
                    values={translationValues}
                  />
                </li>
                <li>
                  <FormattedMessage
                    id="StripeConnectAccountStatusModal.text2"
                    values={translationValues}
                  />
                </li>
                <li>
                  <FormattedMessage
                    id="StripeConnectAccountStatusModal.text3"
                    values={translationValues}
                  />
                  <ul type="a">
                    <li>
                      <FormattedMessage
                        id="StripeConnectAccountStatusModal.text3.subtext1"
                        values={translationValues}
                      />
                    </li>
                    <li>
                      <FormattedMessage
                        id="StripeConnectAccountStatusModal.text3.subtext2"
                        values={translationValues}
                      />
                    </li>
                  </ul>
                </li>
                <li>
                  <FormattedMessage
                    id="StripeConnectAccountStatusModal.text4"
                    values={{
                      ...translationValues,
                      contactSupport: contactSupportLink,
                    }}
                  />
                </li>
              </ul>
            </div>
          </DialogContent>
        </SWDialog>
      </div>
    );
  } else if (type === STATUS_VERIFICATION_SUCCESS) {
    return (
      <div>
        <h3 className={css.verificationBoxHeadline}>
          <FormattedMessage id="StripeConnectAccountStatusBox.verificationNeededHeadline" />
        </h3>

        <div className={classNames(css.verificiationBox, css.verficiationNeededBox)}>
          <div className={css.verificationTopRow}>
            <div className={css.verificiationBoxTextWrapper}>
              <div className={classNames(css.verificationBoxTitle, css.verificationSuccessTitle)}>
                <IconSuccess className={css.icon} />
                <FormattedMessage id="StripeConnectAccountStatusBox.verificationSuccessTitle" />
              </div>
              <div className={css.verificationBoxText}>
                <FormattedMessage id="StripeConnectAccountStatusBox.verificationSuccessText" />
              </div>
            </div>

            <Button
              rootClassName={css.getVerifiedButton}
              spinnerClassName={css.spinner}
              type="button"
              inProgress={inProgress}
              disabled={disabled}
              onClick={onGetStripeConnectAccountLink}
            >
              <FormattedMessage id="StripeConnectAccountStatusBox.editAccountButton" />
            </Button>
          </div>
          <div className={css.verificationFooter}>
            <p className={css.verificationFooterLine}>
              <FormattedMessage id="StripeConnectAccountStatusBox.verificationNeededFooter" />
            </p>
          </div>
        </div>
      </div>
    );
  } else if (type === STATUS_VERIFICATION_ERROR) {
    return (
      <div className={classNames(css.verificiationBox, css.verficiationErrorBox)}>
        <div className={css.verificiationBoxTextWrapper}>
          <div className={css.verificationBoxTitle}>
            <FormattedMessage id="StripeConnectAccountStatusBox.verificationFailedTitle" />
          </div>
          <div className={css.verificationBoxText}>
            <FormattedMessage id="StripeConnectAccountStatusBox.verificationFailedText" />
          </div>
        </div>

        <Button
          rootClassName={css.getVerifiedButton}
          spinnerClassName={css.spinner}
          type="button"
          inProgress={inProgress}
          disabled={disabled}
          onClick={onGetStripeConnectAccountLink}
        >
          <FormattedMessage id="StripeConnectAccountStatusBox.getVerifiedButton" />
        </Button>
      </div>
    );
  } else {
    throw new Error(`Unknown type ${type} for StripeConnectAccountStatusBox`);
  }
};

export default StripeConnectAccountStatusBox;
